import { isPlatformBrowser } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, PLATFORM_ID, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ApplicationService } from 'app-shell-data';
import { NavItemData, NavItemType, NavigationStore } from 'navigation-data';
import { fromEvent } from 'rxjs';
import { PrimaryNavigationBarComponent } from './primary-navigation-bar/primary-navigation-bar.component';
import { SecondaryNavigationMenuComponent } from './secondary-navigation-menu/secondary-navigation-menu.component';
import { SecondaryNavigationMenuComponentStore } from './secondary-navigation-menu/secondary-navigation-menu.store';

const routeItemTypes = new Set<NavItemType>([NavItemType.RouteLink, NavItemType.RouteButton]);

type MenuClickAction = 'keep-open' | 'close';

/**
 * When the menu overlays the page (small screens), it closes after each navigation. Keep it open when the
 * click opens a sub-panel or selects a page in the sub-panel's list, and close it when the button for the
 * sub-panel already showing is pressed again, or when an anchor (a section of the current page, e.g. the
 * product details sections) is selected. Anything else (e.g. content rendered into the sub-panel, like
 * categories) keeps the default close-on-navigation behaviour.
 */
function getMenuClickAction(
  item: NavItemData,
  panelId: string | null,
  panelItems: NavItemData[],
  hasChildren: boolean,
): MenuClickAction | undefined {
  // Closed here rather than on navigation, since re-selecting the current section doesn't navigate.
  if (item.type === NavItemType.Anchor) return 'close';
  if (!routeItemTypes.has(item.type)) return undefined;
  if (item.id === panelId) return 'close';
  if (hasChildren || panelItems.some((panelItem) => panelItem.id === item.id)) return 'keep-open';
  return undefined;
}

@Component({
  selector: 'ideal-navigation-side-menu',
  standalone: true,
  imports: [PrimaryNavigationBarComponent, SecondaryNavigationMenuComponent],
  templateUrl: './navigation-side-menu.component.html',
  styleUrl: './navigation-side-menu.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [SecondaryNavigationMenuComponentStore],
})
export class NavigationSideMenuComponent {
  private readonly _app = inject(ApplicationService);
  private readonly _navStore = inject(NavigationStore);
  private readonly _secondaryNavStore = inject(SecondaryNavigationMenuComponentStore);

  constructor() {
    if (!isPlatformBrowser(inject(PLATFORM_ID))) return;
    // Capture phase so this runs before the item's routerLink starts navigating.
    fromEvent<MouseEvent>(inject(ElementRef).nativeElement, 'click', { capture: true })
      .pipe(takeUntilDestroyed())
      .subscribe((event) => this.onItemClick(event));
  }

  private onItemClick(event: MouseEvent) {
    if (this._app.menu.isInline() || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey) return;

    const itemId = (event.target as Element | null)?.closest('[data-nav-item-id]:not([disabled])')?.getAttribute('data-nav-item-id');
    const item = itemId ? this._navStore.getById(itemId) : undefined;
    if (!item) return;

    const action = getMenuClickAction(
      item,
      this._secondaryNavStore.navigationId(),
      this._secondaryNavStore.navChildren(),
      this._navStore.getChildrenOf(item.id).length > 0,
    );
    if (action === 'close') {
      this._app.menu.close();
    } else if (action === 'keep-open') {
      this._app.menu.keepOpenOnNextNavigation();
    }
  }
}
